import type { Permission } from "@/core/auth/usePermissions";
import { MARQUE, type Marque } from "@dozka/ui";
import type { PanelId } from "@/shell/PanelRegistry";

/**
 * Le KIOSQUE : les rôles dont le cockpit se réduit à une barre d'onglets fixe.
 *
 * Un rôle en kiosque n'a pas le Bureau — ni taskbar, ni workspaces, ni raccourcis de layout, et ses onglets ne se ferment pas (cf
 * `BottomBar` / `SideRail` / `keyboard` / `TabStrip`, qui lisent tous
 * `kioskRoleFor`). Le layout est un override in-memory posé par
 * `useKioskLayout` : rien n'est persisté, un rechargement repart du même
 * squelette.
 *
 * ⚠️ Ce n'est PAS un contrôle d'accès. Les permissions restent la seule
 * autorisation ; le kiosque décide de ce qu'on peut ATTEINDRE à la souris.
 * Un onglet qu'un rôle n'a pas le droit d'ouvrir doit être refusé par sa
 * permission, pas seulement absent d'ici.
 *
 * Ça a vécu six semaines codé en dur sur le chatteur. Kayla, le 26-08-2026,
 * pour le manager chatter : « les chatters, ils n'ont pas tout le bureau, ils
 * ont juste les trois onglets. Lui, c'est pareil. » D'où la généralisation au
 * rôle plutôt qu'un second chemin à côté.
 */
export type KioskRole =
  | "chatter"
  | "manager"
  | "partenaire"
  | "collaborateur"
  | "affilie"
  | "partenaire_rishfans"
  | "espace_cyrano";

/**
 * Les onglets FIXES de chaque rôle en kiosque, dans l'ordre d'affichage.
 *
 * 🪤 Le premier onglet est celui sur lequel le rôle ATTERRIT (`activeIndex: 0`)
 * — l'ordre n'est donc pas décoratif.
 *
 * `conversations` chez le chatteur est conditionnel : il n'apparaît que si un
 * shift lui est ATTRIBUÉ (décision de Kayla, 18-08-2026 — le travail n'apparaît
 * qu'avec le créneau). Le manager, lui, l'a toujours : il supervise, il passe
 * outre les verrous et la fenêtre de shift (cf `BYPASS_ROLE_SLUGS`), donc le
 * lui retirer faute de créneau n'aurait aucun sens.
 */
export const KIOSK_TABS: Record<KioskRole, readonly PanelId[]> = {
  chatter: ["conversations", "formation", "personnel", "documentation"],
  // Le collaborateur externe d'un site vitrine (27-08-2026) : ses deux
  // surfaces, ouvertes d'emblee, et rien autour. Demande de Kayla, mot pour
  // mot : « il a juste ses pages ouvertes, il n'y a pas de plus ».
  //
  // 🪤 Sans le kiosque, il atterrissait sur le canvas VIDE de `buildDefault`
  // avec un « + Ajouter un panneau » — techniquement ouvert, pratiquement
  // introuvable : il faut savoir que le picker existe, et ce qu'on y cherche.
  // `personnel` en dernier n'est pas decoratif : c'est le fond de secours de
  // tout kiosque (il dit « aucune organisation active » quand le reste manque),
  // et un kiosque sans lui serait un ecran noir sans echappatoire.
  partenaire: ["sites", "twitter-sites", "telegram-sites", "personnel"],
  // Le collaborateur X (21-09-2026) : l'app Twitter de Kayla — qui ne lui
  // montre que SON espace, par son scope — et Personnel. Kayla, mot pour mot :
  // « il doit avoir acces a Twitter tout court et Personnel ».
  // Et ses canaux Telegram (30-09-2026) : Kayla veut que Marko génère lui-même
  // les liens de ses comptes X vers Dark Trista. L'app ne lui rend que les
  // canaux dont il est le partenaire — le backend en décide, pas l'onglet.
  collaborateur: ["twitter", "canaux-telegram", "personnel"],
  // L'affilié (10-09-2026) : UN onglet, et c'est tout le cockpit. Kayla, mot
  // pour mot : « j'ai pas envie qu'il ait besoin de cliquer sur l'application.
  // J'ai envie qu'il y ait qu'une seule page. […] Je veux juste qu'il ait son
  // truc personnel où il voit ses stats. »
  //
  // 🪤 Il DÉROGE à la règle du `personnel` en dernier, posée pour le partenaire
  // comme fond de secours (« un kiosque sans lui serait un écran noir sans
  // échappatoire »). Elle ne s'applique pas ici : `affilie` porte son propre
  // écran de repli — sans lien attribué, il affiche « demande à ton contact de
  // relier ton bot », pas du vide. Un second onglet lui donnerait le
  // Portefeuille et les Shifts d'un employé qu'il n'est pas.
  affilie: ["affilie"],
  // Le partenaire RishFans (13-10-2026) : UNE page, `rishfans` — ses chiffres,
  // ses liens d'invitation, son portefeuille. Même doctrine que l'affilié
  // (Kayla : « qu'une seule page »), donc même exemption de `personnel` (voir
  // `KIOSK_SANS_PERSONNEL`) : la page porte son propre repli, « pas encore
  // activé » quand `/api/partenaire/moi` refuse.
  partenaire_rishfans: ["rishfans"],
  // L'espace de travail Cyrano (`cyrano.work`, 13-10-2026) : ce qu'y voit un
  // rôle qui, sur dozka.app, garderait le Bureau — CEO, Operator, VA. Kayla,
  // connectée là-dessus avec son compte : « c'est censé être un espace juste
  // pour les chatters […] il ne devrait y avoir que les trucs du chatting. »
  // Les onglets du chatteur, sans le verrou du shift (cf `kioskRoleFor`).
  espace_cyrano: ["conversations", "formation", "personnel", "documentation"],
  manager: [
    "conversations",
    "telegram",
    "organisation",
    "formation",
    "personnel",
    "documentation",
  ],
};

/**
 * Les rôles dont l'onglet `conversations` est conditionné à un shift ATTRIBUÉ.
 *
 * UNE source pour cette règle : `buildKiosk` s'en sert pour décider s'il a le
 * droit de retirer l'onglet, et `useKioskLayout` pour décider s'il interroge
 * le shift gate. Les deux se sont d'abord écrits `=== "chatter"` chacun de son
 * côté — et le builder retirait alors Conversations à un manager dès qu'on
 * l'appelait avec le drapeau baissé. Un invariant qui vit à deux endroits ne
 * tient qu'aussi longtemps que les deux se relisent.
 */
export const KIOSK_SHIFT_GATED_ROLES: ReadonlySet<KioskRole> = new Set([
  "chatter",
]);

/**
 * Les kiosques dispensés de l'onglet `personnel`.
 *
 * 🩸 `personnel` est le fond de secours de tout kiosque : quand le reste
 * manque, c'est lui qui dit « aucune organisation active » plutôt que de
 * laisser un écran noir sans échappatoire. La règle vaut, et son test la
 * garde.
 *
 * L'affilié en sort parce que sa page porte SON PROPRE repli : sans lien
 * attribué — ou si l'appel échoue — `MonLienTab` affiche « demande à ton
 * contact de relier ton bot », pas du vide. Le danger que `personnel`
 * couvrait n'existe donc pas ici, et lui coller un second onglet lui donnerait
 * le Portefeuille et les Shifts d'un employé qu'il n'est pas.
 *
 * ⛔ L'exemption ne se donne qu'à un kiosque d'UNE page, dont la page porte le
 * nom du rôle — c'est la seule forme où « le reste manque » ne peut pas
 * laisser un onglet mort à côté d'un onglet vivant. Le test le vérifie : y
 * ajouter un rôle multi-onglets échoue.
 */
export const KIOSK_SANS_PERSONNEL: ReadonlySet<KioskRole> = new Set([
  "affilie",
  // Sa page `rishfans` porte son repli (« pas encore activé ») ET le
  // Portefeuille : lui coller `personnel` lui rendrait les Shifts et le
  // Classement d'un employé qu'il n'est pas.
  "partenaire_rishfans",
]);

/**
 * Les rôles built-in mis en kiosque par leur SLUG seul (cf `kioskRoleFor`).
 *
 * Le chatteur, lui, se reconnaît AUSSI à son profil de permissions
 * (`isChatterLike`) — c'est un filet volontairement large. Ceux-ci non : voir
 * le pourquoi dans `kioskRoleFor`.
 */
export const KIOSK_SLUG_ROLES: Readonly<Record<string, KioskRole>> = {
  manager: "manager",
  partenaire: "partenaire",
  collaborateur: "collaborateur",
  // 🩸 Par le slug SEUL, et pas par le profil de permissions — qui serait
  // pourtant tentant : l'affilié est le seul rôle à n'en porter AUCUNE. Mais
  // « zéro permission » attraperait n'importe quel rôle custom que Kayla
  // créerait vide, et l'enfermerait dans un kiosque d'affilié sans que rien ne
  // le dise. Même arbitrage que le manager et le partenaire juste au-dessus.
  affilie: "affilie",
  // Par le slug SEUL, pour la même raison que l'affilié : son profil (zéro
  // permission) attraperait n'importe quel rôle custom créé vide.
  partenaire_rishfans: "partenaire_rishfans",
};

/**
 * Le kiosque de ce caller, ou `null` s'il garde le Bureau complet.
 *
 * Sur l'espace de travail Cyrano (`marque` = Cyrano), PERSONNE ne garde le
 * Bureau : un rôle qui l'aurait ailleurs y reçoit `espace_cyrano`. Les rôles
 * déjà en kiosque gardent le leur — le chatteur son verrou de shift, le
 * manager ses onglets d'équipe.
 *
 * Deux reconnaissances, et elles ne se valent pas :
 *
 *   · **chatteur** — par `isChatterLike`, qui accepte le slug `chatter` ET
 *     tout rôle au profil de chatteur pur (chatte sans gérer les créatrices).
 *     Ce filet large est délibéré : c'est le même que celui qui bloque le
 *     chatteur sur mobile, et un chatteur dupliqué sous un autre nom doit
 *     rester un chatteur.
 *
 *   · **manager** et **partenaire** — par le SLUG SEUL, volontairement. Un
 *     profil de permissions (« a `team.invite` mais pas
 *     `org.manage_settings` », « n'a que `sites.view` ») attraperait n'importe
 *     quel rôle custom que Kayla se fabriquerait, et l'enfermerait dans un
 *     kiosque sans que rien ne le dise. Un filet qui se referme sur un inconnu
 *     est pire que pas de filet : ce sont des slugs built-in, ils sont
 *     stables, ils suffisent.
 *
 * ⚠️ Entrer ici a un COÛT : `mobileAccessGate` lit cette fonction, donc tout
 * rôle ajouté au kiosque perd l'accès au cockpit sur téléphone (« va sur ton
 * ordinateur »). C'est la règle de Kayla du 26-08-2026, et elle est
 * délibérée — mais elle se paie à l'insu de qui ajoute une ligne ici.
 */
export function kioskRoleFor(
  roleSlug: string | null,
  permissions: Set<Permission> | null,
  marque: Marque = MARQUE,
): KioskRole | null {
  if (isChatterLike(roleSlug, permissions)) return "chatter";
  if (roleSlug !== null && roleSlug in KIOSK_SLUG_ROLES) {
    return KIOSK_SLUG_ROLES[roleSlug];
  }
  // Rôle encore inconnu (bootstrap) : on n'enferme personne — le shell rend un
  // canvas neutre tant que les permissions chargent.
  if (marque.id === "cyrano" && (roleSlug !== null || permissions !== null)) {
    return "espace_cyrano";
  }
  return null;
}

/** Le slug du rôle built-in chatteur. */
export const CHATTER_ROLE_SLUG = "chatter";

/**
 * « Chatteur » au sens restreint. On reconnaît :
 *   1. le rôle built-in `chatter` (slug exact), ET
 *   2. tout rôle au **profil de chatteur pur** : peut chatter
 *      (`conversations.chat`) mais n'a PAS la gestion des créateurs
 *      (`creators.view`). Ça exclut operator/manager/CEO (qui ont
 *      `creators.view`) et VA (qui n'a pas `conversations.chat`).
 *
 * Le point (2) rend la reconnaissance robuste aux **rôles personnalisés**
 * calqués sur un chatteur : on ne dépend plus du slug exact. C'est ce qui
 * garantit qu'un chatteur renommé ou dupliqué reste traité comme un chatteur.
 *
 * 🪤 Cette fonction a vécu dans `mobileAccessGate.ts` jusqu'au 26-08-2026.
 * Elle est remontée ici le jour où le blocage mobile a cessé de viser le
 * chatteur pour viser le KIOSQUE : `mobileAccessGate` appelle désormais
 * `kioskRoleFor`, et la laisser en bas aurait fait un cycle d'imports entre
 * les deux fichiers. La flèche ne va plus que dans un sens — le kiosque ne
 * sait rien du mobile, le mobile lit le kiosque.
 */
export function isChatterLike(
  roleSlug: string | null,
  permissions: Set<Permission> | null,
): boolean {
  if (roleSlug === CHATTER_ROLE_SLUG) return true;
  if (!permissions) return false;
  return (
    permissions.has("conversations.chat") && !permissions.has("creators.view")
  );
}
